import { ChevronRight } from 'assets/icons/ChevronRight';
import LoadingIndicator from 'components/common/LoadingIndicator';
import { Mode } from 'enums';
import { ROUTES } from 'constants/routes';
import { ATTRIB } from 'constants/test';
import { IconProps, MenuItem } from 'interfaces';
import {
  cloneElement,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { navMenuSelector } from 'store/reducer/setting/selectors';
import {
  configureTabs,
  setGeneratorMode,
  updateIsSettingEditingFinished,
  updatePreviousSettings
} from 'store/reducer/setting/slice';
import { getNavigateOptions } from 'utils/navBar/sideNav.util';

interface SideNavItemProps {
  text?: string;
  path?: string;
  icon?: React.ReactNode;
  subMenus?: MenuItem[];
  attribAction?: string;
  isCollapsed: boolean;
}

const renderMenuIcon = (icon: ReactNode) => (
  <span className='flex-shrink-0 text-current'>
    {cloneElement(icon as ReactElement<IconProps>, {
      fill: 'currentColor',
      fillDarkMode: 'currentColor',
      className: '~w-3.5/5 ~h-3.5/5'
    })}
  </span>
);

const SideNavItem = ({
  text,
  path,
  icon,
  subMenus,
  attribAction,
  isCollapsed
}: SideNavItemProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentSettings = useAppSelectorShallow(navMenuSelector);
  const { pathname } = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedNestedIds, setExpandedNestedIds] = useState<
    Record<string, boolean>
  >({});

  const isCurrentPage = path && pathname.endsWith(path);
  const hasSubMenus = subMenus && subMenus.length > 0;

  const handleClick = useCallback(() => {
    if (hasSubMenus) {
      setIsExpanded((prev) => !prev);
      return;
    }

    if (path) {
      navigate(path, getNavigateOptions(path));
      if (path.includes(ROUTES.SETTINGS)) {
        dispatch(setGeneratorMode(Mode.DEFAULT));
        dispatch(configureTabs({ mode: Mode.DEFAULT }));
        dispatch(updatePreviousSettings(currentSettings));
        dispatch(updateIsSettingEditingFinished(false));
      }
    }
  }, [hasSubMenus, path, navigate, dispatch, currentSettings]);

  const getMenuKey = useCallback((menuItem: MenuItem) => {
    if (menuItem.path) return menuItem.path;
    if (menuItem.id) return menuItem.id;
    return menuItem.text || '';
  }, []);

  const handleSubMenuClick = useCallback(
    (menuItem: MenuItem) => {
      if (menuItem.path) {
        navigate(menuItem.path, getNavigateOptions(menuItem.path));
      }
    },
    [navigate]
  );

  const handleParentSubMenuClick = useCallback(
    (menuItem: MenuItem) => {
      const hasNested = Boolean(menuItem.subMenus?.length);
      if (!hasNested) {
        handleSubMenuClick(menuItem);
        return;
      }
      const key = getMenuKey(menuItem);
      setExpandedNestedIds((prev) => ({
        ...prev,
        [key]: !prev[key]
      }));
    },
    [getMenuKey, handleSubMenuClick]
  );

  const getSubMenuButtonClassName = useCallback(
    (itemPath?: string) => {
      const isSubMenuActive = itemPath && pathname.endsWith(itemPath);
      return `w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
        isSubMenuActive
          ? 'bg-primary-50 dark:bg-secondary-dark-300 text-primary-900 dark:text-white font-medium'
          : 'text-gray-600 dark:text-secondary-200 hover:bg-primary-100 dark:hover:bg-secondary-dark-300 hover:text-primary-900 dark:hover:text-white'
      }`;
    },
    [pathname]
  );

  const iconElement = useMemo(() => {
    if (!icon) return null;
    return (
      <span
        className={`flex-shrink-0 transition-colors duration-200 ${
          isCurrentPage
            ? 'text-primary-900 dark:text-white'
            : 'text-gray-900 dark:text-secondary-100 group-hover:text-primary-900 dark:group-hover:text-white'
        }`}>
        {cloneElement(icon as ReactElement<IconProps>, {
          fill: 'currentColor',
          fillDarkMode: 'currentColor',
          className: '~w-4/5 ~h-4/5'
        })}
      </span>
    );
  }, [icon, isCurrentPage]);

  const renderSubMenuLabel = ({
    menuItem,
    showChevron = false,
    isChevronExpanded = false
  }: {
    menuItem: MenuItem;
    showChevron?: boolean;
    isChevronExpanded?: boolean;
  }) => (
    <span className='flex items-center justify-between gap-2'>
      <span className='flex items-center ~gap-1/2 min-w-0'>
        {menuItem.icon ? renderMenuIcon(menuItem.icon) : null}
        <span className='truncate'>{menuItem.text}</span>
        {menuItem.isLoading ? (
          <LoadingIndicator
            dims='~w-3/3.5 ~h-3/3.5'
            additionalStyles='flex-shrink-0'
          />
        ) : null}
      </span>
      {showChevron ? (
        <ChevronRight
          styles={`~w-3/3.5 ~h-3/3.5 flex-shrink-0 transition-transform duration-200 ${
            isChevronExpanded ? 'rotate-90' : ''
          }`}
          fill='currentColor'
        />
      ) : null}
    </span>
  );

  return (
    <div className='w-full'>
      <button
        onClick={handleClick}
        data-test={attribAction}
        data-testid={ATTRIB.DATA_TESTID.NAV_MENU_MAIN_BTN}
        className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
          isCurrentPage
            ? 'bg-primary-50 dark:bg-secondary-dark-300 text-primary-900 dark:text-white'
            : 'text-gray-900 dark:text-secondary-100 hover:bg-primary-100 dark:hover:bg-secondary-dark-300 hover:text-primary-900 dark:hover:text-white'
        } ${isCollapsed ? 'justify-center' : ''}`}
        title={isCollapsed ? text : undefined}>
        {iconElement}
        {!isCollapsed && (
          <>
            <span className='flex-1 text-left text-sm font-semibold truncate'>
              {text}
            </span>
            {hasSubMenus && (
              <ChevronRight
                styles={`w-3 h-3 transition-transform duration-200 ${
                  isExpanded ? 'rotate-90' : 'rotate-0'
                }`}
                fill='currentColor'
              />
            )}
          </>
        )}
      </button>

      {hasSubMenus && isExpanded && !isCollapsed && (
        <div className='ml-4 mt-1 space-y-1 border-l-2 border-primary-200 dark:border-secondary-dark-300 pl-3'>
          {subMenus.map((subMenu) => {
            const hasNestedSubMenus = Boolean(subMenu.subMenus?.length);
            const key = getMenuKey(subMenu);
            const isNestedExpanded = expandedNestedIds[key];

            if (hasNestedSubMenus) {
              return (
                <div key={key} className='space-y-1'>
                  <button
                    onClick={() => handleParentSubMenuClick(subMenu)}
                    data-test={subMenu.attribAction}
                    aria-haspopup='true'
                    aria-expanded={isNestedExpanded}
                    aria-busy={subMenu.isLoading}
                    className={getSubMenuButtonClassName(subMenu.path)}>
                    {renderSubMenuLabel({
                      menuItem: subMenu,
                      showChevron: true,
                      isChevronExpanded: isNestedExpanded
                    })}
                  </button>

                  {isNestedExpanded && (
                    <div className='ml-3 border-l-2 border-primary-200 dark:border-secondary-dark-300 pl-3 space-y-1'>
                      {subMenu.subMenus?.map((nestedMenu) => (
                        <button
                          key={nestedMenu.path || nestedMenu.text}
                          onClick={() =>
                            handleSubMenuClick(nestedMenu as MenuItem)
                          }
                          data-test={nestedMenu.attribAction}
                          className={getSubMenuButtonClassName(
                            nestedMenu.path
                          )}>
                          {renderSubMenuLabel({ menuItem: nestedMenu })}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={subMenu.path || subMenu.text}
                onClick={() => {
                  if (subMenu.isLoading) return;
                  handleSubMenuClick(subMenu);
                }}
                data-test={subMenu.attribAction}
                aria-busy={subMenu.isLoading}
                disabled={subMenu.isLoading}
                className={getSubMenuButtonClassName(subMenu.path)}>
                {renderSubMenuLabel({ menuItem: subMenu })}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
export default SideNavItem;
